import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  Audience,
  IExperienceTemplate,
  TaskBlockTemplate
} from '@dadohr/shared/domainTypes/experience';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { Theme } from '@emotion/react';
import { useMemo } from 'react';
import { UnsafeTFunction } from '../../i18next';
import { Timestamp } from '../time';
import { matchUser } from '../user';
import {
  getAutomationTemplateById,
  getMessageTemplateById,
  getTaskTemplateById
} from './template';

export const getEveryoneAudience = <A extends Audience>(audiences: A[]) => {
  const audience = audiences.find((a) => a.rule === 'EVERYONE');
  if (!audience) {
    throw new Error(
      'MISCONFIGURED_AUDIENCES - audience for everyone should always be there'
    );
  }
  return audience;
};

export const getAudienceForUser = <A extends Audience>(
  audiences: A[],
  user: Doc<IUser>,
  referenceDate: Timestamp
) => {
  const matchingAudience = audiences.find((a) => {
    return a.rule !== 'EVERYONE' && matchUser(user, a.rule, referenceDate);
  });
  return matchingAudience || getEveryoneAudience(audiences);
};

export const getAudienceById = <A extends Audience>(
  audiences: A[],
  id: string,
  throwIfNotFound = false
) => {
  const matchingAudience = audiences.find((a) => a.id === id);
  if (!matchingAudience) {
    if (throwIfNotFound) {
      throw new Error('AUDIENCE_NOT_FOUND');
    }
    console.warn('AUDIENCE_NOT_FOUND - fallback to everyone', {
      audiences,
      audienceId: id
    });
    return getEveryoneAudience(audiences);
  }
  return matchingAudience;
};

export const filterBlocksForAudience = <
  T extends TaskBlockTemplate,
  A extends Audience
>(
  bs: T[],
  audience: A
): T[] => {
  return bs.filter((b) => audience.blocks.includes(b.id));
};

/**
 * FE mirror of the backend `getEffectiveAudiences`
 * (`new-main/packages/shared/src/services/experiences/helpers.ts`). A
 * DEACTIVATED timeline item resolves to zero visible blocks for every audience,
 * so it drops out of the client-side materialization/recalc mirror exactly as it
 * does on the server — e.g. the manual-enroll preview no longer lists it. The
 * stored config is not mutated; audience selection keys on `rule`, not blocks.
 */
export const getEffectiveAudiences = <A extends Audience>(item: {
  deactivated?: boolean;
  audiences: A[];
}): A[] => {
  if (!item.deactivated) return item.audiences;
  return item.audiences.map((a) => ({ ...a, blocks: [] }));
};

export const getAudienceName = <A extends Audience>(
  audience: A,
  hasOtherAudiences: boolean,
  t: UnsafeTFunction,
  opts: { noDefault?: boolean; hideInactiveLabel?: boolean } = {}
) => {
  const withDeletedHint = (x: string) =>
    audience.inactive && !opts.hideInactiveLabel ? `${x} (deleted)` : x;
  if (audience.rule === 'EVERYONE') {
    const defaultName = hasOtherAudiences ? t('Everyone else') : t('Everyone');
    if (opts.noDefault) {
      // Raw editable value for the rename input — never the composed/suffixed form.
      return withDeletedHint(audience.name || '');
    }
    return withDeletedHint(
      audience.name ? `${audience.name} (${defaultName})` : defaultName
    );
  }
  const otherName =
    audience.name || (opts.noDefault ? '' : t('Unnamed Audience'));
  return withDeletedHint(otherName);
};

/**
 * A custom name on the EVERYONE audience only makes sense once it's actually the
 * "else" in "Everyone else" — once every other segment is removed, drop it so it
 * doesn't linger as a stale name on the sole/default audience.
 */
export const clearEveryoneNameIfSole = <A extends Audience>(
  audiences: A[]
): A[] => {
  if (
    audiences.length === 1 &&
    audiences[0].rule === 'EVERYONE' &&
    audiences[0].name
  ) {
    return [{ ...audiences[0], name: '' }];
  }
  return audiences;
};

export const getTaskAudiencesFromTemplateById = (
  tpl: Doc<IExperienceTemplate>,
  taskTplId: string,
  audienceId: string
) => {
  const taskTpl = getTaskTemplateById(tpl, taskTplId);
  return taskTpl ? getAudienceById(taskTpl.audiences, audienceId) : null;
};

export const getMessageAudiencesFromTemplateById = (
  tpl: Doc<IExperienceTemplate>,
  msgTplId: string,
  audienceId: string
) => {
  const msgTpl = getMessageTemplateById(tpl, msgTplId);
  return msgTpl ? getAudienceById(msgTpl.audiences, audienceId) : null;
};

export const getAutomationAudiencesFromTemplateById = (
  tpl: Doc<IExperienceTemplate>,
  autTplId: string,
  audienceId: string
) => {
  const autTpl = getAutomationTemplateById(tpl, autTplId);
  return autTpl ? getAudienceById(autTpl.audiences, audienceId) : null;
};

export const getAudienceColors = (theme: Theme) => {
  const cs = theme.custom.colors;
  return [
    cs.blue.w300,
    cs.green.w300,
    cs.yellow.w300,
    cs.red.w300,
    cs.purple.w300,
    cs.blue.w500,
    cs.green.w500,
    cs.yellow.w500,
    cs.red.w500,
    cs.purple.w500,
    cs.blue.w700,
    cs.green.w700,
    cs.yellow.w700,
    cs.red.w700,
    cs.purple.w700
  ];
};

export const useAudienceColors = (theme: Theme) => {
  return useMemo(() => getAudienceColors(theme), [theme]);
};

export const getRandomAudienceColor = (theme: Theme) => {
  const colors = getAudienceColors(theme);
  return colors[Math.floor(Math.random() * colors.length)];
};

export const getRandomAudienceColorExceptPreviousColor = (
  theme: Theme,
  previousColor: string
) => {
  const colors = getAudienceColors(theme);
  const nextColor = colors[Math.floor(Math.random() * colors.length)];

  if (nextColor === previousColor) {
    getRandomAudienceColorExceptPreviousColor(theme, previousColor);
  }

  return nextColor;
};
